import { pageDrafts } from "@/lib/visitor-storage";
import type { DraftSnapshot, DraftStore } from "./draft-store";

const EMPTY: DraftSnapshot = { text: "", html: "" };

/**
 * Unsent drafts, per thread, for the lifetime of the page. The messages tab remounts every time
 * the visitor leaves a thread (list, other tabs), and its draft store is per mount, so without
 * this the visitor lost whatever they had typed. Memory only: nothing about a draft is written to
 * storage, a reload starts clean.
 */
const drafts: Map<string, DraftSnapshot> = pageDrafts;

/**
 * Key of one thread's draft. Scoped by integration (`publicKey`) so two widgets on one page never
 * share a draft; the whole map is wiped on shutdown / sign-out (`clearDraftSessions`).
 */
export function draftSessionKey(publicKey: string | null, threadKey: string | null): string {
  return `${publicKey ?? ""}\u0000${threadKey ?? "main"}`;
}

export function loadDraftSession(
  threadKey: string,
  map: Map<string, DraftSnapshot> = drafts,
): DraftSnapshot {
  return map.get(threadKey) ?? EMPTY;
}

/** Mirror `store` into the session map under `threadKey`; returns the unsubscribe. */
export function bindDraftToSession(
  store: DraftStore,
  threadKey: string,
  map: Map<string, DraftSnapshot> = drafts,
): () => void {
  return store.subscribe(() => {
    const snap = store.get();
    if (snap.text === "" && snap.html === "") map.delete(threadKey);
    else map.set(threadKey, snap);
  });
}

/** Forget every unsent draft: the next visitor on a shared device must never see the last one's. */
export function clearDraftSessions(map: Map<string, DraftSnapshot> = drafts): void {
  map.clear();
}
